// src/routes/AppRoutes.js
// Configuration du routage Fynho - NE PAS MODIFIER

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ROUTES from '../constants/routes';

// Pages publiques
import Home from '../pages/Home';
import Search from '../pages/Search';
import MapSearch from '../pages/MapSearch';
import ListingDetail from '../pages/ListingDetail';
import Sejours from '../pages/Sejours';
import Locations from '../pages/Locations';
import Immobilier from '../pages/Immobilier';
import Services from '../pages/Services';
import Favorites from '../pages/Favorites';
import Reservations from '../pages/Reservations';

// Pages auth (optionnelles)
import Login from '../pages/auth/Login';
import Register from '../pages/auth/Register';
import ForgotPassword from '../pages/auth/ForgotPassword';
import ResetPassword from '../pages/auth/ResetPassword';

// Pages utilisateur
import Profile from '../pages/Profile';
import Notifications from '../pages/user/Notifications';
import Messages from '../pages/user/Messages';
import Settings from '../pages/user/Settings';
import Support from '../pages/user/Support';

// Pages admin
import AdminLogin from '../pages/admin/AdminLogin';
import AdminDashboard from '../pages/admin/AdminDashboard';
import AdminReservations from '../pages/admin/AdminReservations';
import AdminListings from '../pages/admin/AdminListings';
import AdminAddListing from '../pages/admin/AdminAddListing';
import AdminEditListing from '../pages/admin/AdminEditListing';
import AdminOwners from '../pages/admin/AdminOwners';
import AdminQuartiers from '../pages/admin/AdminQuartiers';
import AdminTypes from '../pages/admin/AdminTypes';
import AdminUsers from '../pages/admin/AdminUsers';
import AdminAdmins from '../pages/admin/AdminAdmins';
import AdminStats from '../pages/admin/AdminStats';
import AdminAuditLogs from '../pages/admin/AdminAuditLogs';

// Layouts
import MainLayout from '../layouts/MainLayout';

// Protected Route
const ProtectedRoute = ({ children, isAuthenticated }) => {
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  return children;
};

const AdminProtectedRoute = ({ children, isAuthenticated, isAdmin }) => {
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to={ROUTES.ADMIN_LOGIN} replace />;
  }
  return children;
};

/**
 * Configuration du routage complet de l'app
 */
const AppRoutes = () => {
  // TODO: Obtenir l'état d'authentification depuis le context
  const isAuthenticated = false;
  const isAdmin = false;

  return (
    <Router>
      <Routes>
        {/* Routes publiques avec MainLayout */}
        <Route element={<MainLayout />}>
          {/* Accueil */}
          <Route path={ROUTES.HOME} element={<Home />} />

          {/* Catégories */}
          <Route path={ROUTES.SEJOURS} element={<Sejours />} />
          <Route path={ROUTES.LOCATIONS} element={<Locations />} />
          <Route path={ROUTES.IMMOBILIER} element={<Immobilier />} />
          <Route path={ROUTES.SERVICES} element={<Services />} />

          {/* Détail et recherche */}
          <Route path={ROUTES.LISTING_DETAIL} element={<ListingDetail />} />
          <Route path={ROUTES.SEARCH} element={<Search />} />
          <Route path={ROUTES.MAP_SEARCH} element={<MapSearch />} />

          {/* Favoris et réservations (public) */}
          <Route path={ROUTES.FAVORITES} element={<Favorites />} />
          <Route path={ROUTES.RESERVATIONS} element={<Reservations />} />

          {/* Auth (optionnel) */}
          <Route path={ROUTES.LOGIN} element={<Login />} />
          <Route path={ROUTES.REGISTER} element={<Register />} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
          <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />

          {/* Utilisateur connecté */}
          <Route
            path={ROUTES.PROFILE}
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.NOTIFICATIONS}
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.MESSAGES}
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS}
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SUPPORT}
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Support />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Routes admin */}
        {/* Login admin (pas de layout) */}
        <Route path={ROUTES.ADMIN_LOGIN} element={<AdminLogin />} />

        {/* Routes admin protégées */}
        <Route
          path={ROUTES.ADMIN_DASHBOARD}
          element={
            <AdminProtectedRoute isAuthenticated={isAdmin} isAdmin={isAdmin}>
              <AdminDashboard />
            </AdminProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_LISTINGS}
          element={
            <AdminProtectedRoute isAuthenticated={isAdmin} isAdmin={isAdmin}>
              <AdminListings />
            </AdminProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_ADD_LISTING}
          element={
            <AdminProtectedRoute isAuthenticated={isAdmin} isAdmin={isAdmin}>
              <AdminAddListing />
            </AdminProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_EDIT_LISTING}
          element={
            <AdminProtectedRoute isAuthenticated={isAdmin} isAdmin={isAdmin}>
              <AdminEditListing />
            </AdminProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_RESERVATIONS}
          element={
            <AdminProtectedRoute isAuthenticated={isAdmin} isAdmin={isAdmin}>
              <AdminReservations />
            </AdminProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_OWNERS}
          element={
            <AdminProtectedRoute isAuthenticated={isAdmin} isAdmin={isAdmin}>
              <AdminOwners />
            </AdminProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_QUARTIERS}
          element={
            <AdminProtectedRoute isAuthenticated={isAdmin} isAdmin={isAdmin}>
              <AdminQuartiers />
            </AdminProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_TYPES}
          element={
            <AdminProtectedRoute isAuthenticated={isAdmin} isAdmin={isAdmin}>
              <AdminTypes />
            </AdminProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_USERS}
          element={
            <AdminProtectedRoute isAuthenticated={isAdmin} isAdmin={isAdmin}>
              <AdminUsers />
            </AdminProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_ADMINS}
          element={
            <AdminProtectedRoute isAuthenticated={isAdmin} isAdmin={isAdmin}>
              <AdminAdmins />
            </AdminProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_STATS}
          element={
            <AdminProtectedRoute isAuthenticated={isAdmin} isAdmin={isAdmin}>
              <AdminStats />
            </AdminProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_AUDIT_LOGS}
          element={
            <AdminProtectedRoute isAuthenticated={isAdmin} isAdmin={isAdmin}>
              <AdminAuditLogs />
            </AdminProtectedRoute>
          }
        />

        {/* Route 404 */}
        <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;